import { useSyncExternalStore, useCallback } from 'react';

export const HEADER_STORAGE_KEY = 'maleva_app_header_hidden';
export const HEADER_TOGGLE_EVENT = 'maleva:toggle-header';

let currentHidden = false;
try {
  currentHidden = typeof localStorage !== 'undefined' && localStorage.getItem(HEADER_STORAGE_KEY) === 'true';
} catch {
  // Ignore storage errors
}

const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore listener errors
    }
  });
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot() {
  return currentHidden;
}

export function readStoredHeaderHidden(): boolean {
  try {
    return localStorage.getItem(HEADER_STORAGE_KEY) === 'true';
  } catch {
    return currentHidden;
  }
}

export function setHeaderHiddenState(hidden: boolean | ((prev: boolean) => boolean)): void {
  const next = typeof hidden === 'function' ? hidden(currentHidden) : hidden;
  if (currentHidden === next) return;
  currentHidden = next;
  try {
    localStorage.setItem(HEADER_STORAGE_KEY, String(next));
  } catch {
    // Ignore storage errors
  }
  notifyListeners();
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(HEADER_TOGGLE_EVENT, { detail: { hidden: next } }));
    } catch {
      // Ignore event errors
    }
  }
}

export function writeStoredHeaderHidden(hidden: boolean): void {
  setHeaderHiddenState(hidden);
}

export function toggleHeaderState(): void {
  setHeaderHiddenState(!currentHidden);
}

/**
 * Hook to manage app top navigation header visibility.
 * Backed by useSyncExternalStore for flawless React 19 synchronization across all components.
 */
export function useHeaderVisibility() {
  const isHeaderHidden = useSyncExternalStore(subscribe, getSnapshot, () => false);

  const setHeaderHidden = useCallback((hidden: boolean | ((prev: boolean) => boolean)) => {
    setHeaderHiddenState(hidden);
  }, []);

  const toggleHeader = useCallback(() => {
    toggleHeaderState();
  }, []);

  return {
    isHeaderHidden,
    setHeaderHidden,
    toggleHeader,
  };
}
